/**
 * FikoKatiMotoru.ts — Boolean (CSG) + yüz gruplama (motor çekirdeği)
 *
 * three-bvh-csg v0.0.17 ile union/subtract/intersect. Boolean sonrası faceId
 * kaybolduğu için `yuzGrupla()` koplanar üçgenleri tekrar yüzlere ayırır
 * (grup.z = faceId) — bu sayede üretilen katı YÜZE SKETCH için seçilebilir kalır.
 *
 * Brush iki-manifold/watertight ister; extrude/lathe kapalı mesh üretmeli.
 */

import * as THREE from 'three';
import { Evaluator, Brush, ADDITION, SUBTRACTION, INTERSECTION } from 'three-bvh-csg';
import { toCreasedNormals, mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

const KIRISIM_ACI = 25 * Math.PI / 180; // bu açının üstü = keskin kenar (crease)

export class FikoKatiMotoru {
  private ev = new Evaluator();

  constructor() {
    this.ev.attributes = ['position', 'normal'];
    this.ev.useGroups = false;
  }

  birlestir(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry { return this.boolOp(a, b, ADDITION); }
  cikar(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry { return this.boolOp(a, b, SUBTRACTION); }
  kesistir(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry { return this.boolOp(a, b, INTERSECTION); }

  // --- HAM boolean (pürüzsüz/grup YOK) — zincir/pattern içinde kullan, sonunda finalize() çağır ---
  birlestirHam(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry { return this.boolOpHam(a, b, ADDITION); }
  cikarHam(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry { return this.boolOpHam(a, b, SUBTRACTION); }
  kesistirHam(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry { return this.boolOpHam(a, b, INTERSECTION); }

  /** Zincir boolean bittiğinde TEK SEFER: weld (watertight) + pürüzsüz normal + yüz gruplama. */
  finalize(geo: THREE.BufferGeometry): THREE.BufferGeometry {
    let w = geo; try { w = mergeVertices(geo, 1e-3); if (w !== geo) geo.dispose(); } catch { w = geo; }   // lathe seam/primitif/CSG dikişi → watertight
    const g = this.puruzsuzNormal(w); this.yuzGrupla(g); return g;
  }

  /**
   * GERÇEK SHELL iç gövdesi: her vertex'i PAYLAŞILAN (ortalama) normal boyunca −t içe iter
   * (bbox ölçekli değil → silindir/karmaşık gövdede de doğru kabuk). acikNoktalar verilen
   * vertex pozisyonları offset EDİLMEZ (o yüzde iç=dış → gövde−iç o yüzü AÇAR = CATIA açık kabuk).
   */
  kabukIcGeoGercek(geo: THREE.BufferGeometry, t: number, acikNoktalar?: Set<string>): THREE.BufferGeometry | null {
    let ic: THREE.BufferGeometry;
    try { ic = mergeVertices(geo.clone(), 1e-4); } catch { ic = geo.clone(); }   // paylaşılan vertex → ortalama normal
    ic.computeVertexNormals();
    const pos = ic.attributes['position'] as THREE.BufferAttribute;
    const nor = ic.attributes['normal'] as THREE.BufferAttribute;
    if (!pos || !nor) return null;
    const Q = 0.3, key = (x: number, y: number, z: number) => `${Math.round(x / Q)},${Math.round(y / Q)},${Math.round(z / Q)}`;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      if (acikNoktalar && acikNoktalar.has(key(x, y, z))) continue;   // açık yüz → offset yok (dış konum)
      pos.setX(i, x - nor.getX(i) * t); pos.setY(i, y - nor.getY(i) * t); pos.setZ(i, z - nor.getZ(i) * t);
    }
    pos.needsUpdate = true;
    ic.computeVertexNormals();
    return ic;
  }

  private boolOpHam(a: THREE.BufferGeometry, b: THREE.BufferGeometry, op: any): THREE.BufferGeometry {
    const ba = new Brush(this.temiz(a));
    const bb = new Brush(this.temiz(b));
    ba.updateMatrixWorld(true); bb.updateMatrixWorld(true);
    const r = this.ev.evaluate(ba, bb, op);
    let g = r.geometry.clone();
    r.geometry.dispose();
    // KRİTİK: CSG çıktısı kaynaklanmamış vertex bırakır (kesim dikişi) → topolojik AÇIK.
    // mergeVertices ile weld → watertight (downstream fillet/chamfer + gölgelendirme doğru).
    try { const m = mergeVertices(g, 1e-3); g.dispose(); g = m; } catch { }
    return g;
  }

  private boolOp(a: THREE.BufferGeometry, b: THREE.BufferGeometry, op: any): THREE.BufferGeometry {
    return this.finalize(this.boolOpHam(a, b, op));
  }

  /**
   * CATIA görünümü: eğri yüzleri pürüzsüz gölgelendirir, keskin kenarlarda
   * (>KIRISIM_ACI) crease bırakır → faceted/üçgen görünüm kalkar.
   * Pozisyonları korur (yuzGrupla sonradan doğru çalışır). Yeni geometri döndürür.
   */
  puruzsuzNormal(geo: THREE.BufferGeometry): THREE.BufferGeometry {
    try { return toCreasedNormals(geo, KIRISIM_ACI); }
    catch { geo.computeVertexNormals(); return geo; }
  }

  /** CSG girişi için temiz geometri: sadece position+normal, watertight. */
  private temiz(g: THREE.BufferGeometry): THREE.BufferGeometry {
    const c = g.clone();
    for (const ad of Object.keys(c.attributes)) if (ad !== 'position' && ad !== 'normal') c.deleteAttribute(ad);
    if (!c.attributes['normal']) c.computeVertexNormals();
    return c;
  }

  /**
   * Koplanar üçgenleri yüzlere ayırır + üçgenleri yüze göre BİTİŞİK sıralar →
   * `grup` attribute her vertex'te (x=strt, y=vertexCount, z=faceId) — içe-aktarılan
   * WRL mesh'leriyle BİREBİR aynı format. Böylece FikoSecililer.buildGeometryFromGroup
   * (strt+syi ile blok çıkarır) CSG/katı gövdelerde de yüz seçimini NATIVE yapar.
   * (Eski format strt=0,syi=0 yazıyordu → vertexCount=0 → yüz seçimi null dönüyordu.)
   */
  yuzGrupla(geo: THREE.BufferGeometry, keskinAci = 30): void {
    const pos = geo.attributes.position as THREE.BufferAttribute;
    if (!pos) return;
    const idx = geo.index;
    const nor = geo.attributes.normal as THREE.BufferAttribute | undefined;
    const ucgen = idx ? idx.count / 3 : pos.count / 3;
    const vIdx = (t: number, k: number) => idx ? idx.getX(t * 3 + k) : t * 3 + k;
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
    const e1 = new THREE.Vector3(), e2 = new THREE.Vector3(), n = new THREE.Vector3();

    // 1) Her üçgenin geometrik normali + paylaşılan-kenar komşuluk grafiği (manifold)
    const triN = new Float32Array(ucgen * 3);
    const Q = 1e-3, qk = (v: number) => Math.round(v / Q);
    const kKey = (p: THREE.Vector3, r: THREE.Vector3) => {
      const ka = `${qk(p.x)},${qk(p.y)},${qk(p.z)}`, kb = `${qk(r.x)},${qk(r.y)},${qk(r.z)}`;
      return ka < kb ? ka + '|' + kb : kb + '|' + ka;
    };
    const kenarTri = new Map<string, number[]>();   // paylaşılan kenar → komşu üçgenler
    const vt = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
    for (let t = 0; t < ucgen; t++) {
      vt[0].fromBufferAttribute(pos, vIdx(t, 0)); vt[1].fromBufferAttribute(pos, vIdx(t, 1)); vt[2].fromBufferAttribute(pos, vIdx(t, 2));
      e1.subVectors(vt[1], vt[0]); e2.subVectors(vt[2], vt[0]); n.crossVectors(e1, e2);
      if (n.lengthSq() > 1e-12) n.normalize();
      triN[t * 3] = n.x; triN[t * 3 + 1] = n.y; triN[t * 3 + 2] = n.z;
      for (let k = 0; k < 3; k++) { const key = kKey(vt[k], vt[(k + 1) % 3]); let arr = kenarTri.get(key); if (!arr) { arr = []; kenarTri.set(key, arr); } arr.push(t); }
    }

    // 2) HİBRİT union-find: (a) smooth komşu üçgenler (silindir/küre yan yüzü TEK faceId) +
    //    (b) koplanar üçgenler komşuluk olmadan da (CSG T-junction'a DAYANIKLI düz yüz birleştirme).
    //    → silindir = 1 yüz, kutu = 6 yüz, delikli düz yüz tek parça = BREP-uyumlu.
    const cosThr = Math.cos(keskinAci * Math.PI / 180);
    const ebeveyn = new Int32Array(ucgen); for (let i = 0; i < ucgen; i++) ebeveyn[i] = i;
    const bul = (x: number): number => { while (ebeveyn[x] !== x) { ebeveyn[x] = ebeveyn[ebeveyn[x]]; x = ebeveyn[x]; } return x; };
    const birle = (x: number, y: number) => { const rx = bul(x), ry = bul(y); if (rx !== ry) ebeveyn[rx] = ry; };
    // (a) komşuluk smooth union
    for (const tris of kenarTri.values()) {
      for (let i = 0; i < tris.length; i++) for (let j = i + 1; j < tris.length; j++) {
        const t = tris[i], tk = tris[j];
        const dot = triN[t * 3] * triN[tk * 3] + triN[t * 3 + 1] * triN[tk * 3 + 1] + triN[t * 3 + 2] * triN[tk * 3 + 2];
        if (dot >= cosThr) birle(t, tk);
      }
    }
    // (b) koplanar union (aynı normal + aynı düzlem offset → tek yüz, komşuluk gerekmez)
    const aciTol = 0.05, ofsetTol = 0.4, qq = (v: number, tol: number) => Math.round(v / tol);
    const koplanar = new Map<string, number>();
    for (let t = 0; t < ucgen; t++) {
      const nx = triN[t * 3], ny = triN[t * 3 + 1], nz = triN[t * 3 + 2];
      a.fromBufferAttribute(pos, vIdx(t, 0));
      const ofset = nx * a.x + ny * a.y + nz * a.z;
      const key = `${qq(nx, aciTol)}_${qq(ny, aciTol)}_${qq(nz, aciTol)}_${qq(ofset, ofsetTol)}`;
      const onceki = koplanar.get(key);
      if (onceki != null) birle(t, onceki); else koplanar.set(key, t);
    }
    // 3) DETERMİNİSTİK faceId: her yüzün geometrik imzası (ortalama normal + merkez) ile sırala →
    //    fiziksel yüz, topoloji/parametre değişse de AYNI faceId'yi alır (rebuild kalıcılığı, BREP-stabil).
    const kokVeri = new Map<number, { cx: number; cy: number; cz: number; nx: number; ny: number; nz: number; n: number }>();
    for (let t = 0; t < ucgen; t++) {
      const r = bul(t);
      let v = kokVeri.get(r); if (!v) { v = { cx: 0, cy: 0, cz: 0, nx: 0, ny: 0, nz: 0, n: 0 }; kokVeri.set(r, v); }
      let mx = 0, my = 0, mz = 0;
      for (let k = 0; k < 3; k++) { const s = vIdx(t, k); mx += pos.getX(s); my += pos.getY(s); mz += pos.getZ(s); }
      v.cx += mx / 3; v.cy += my / 3; v.cz += mz / 3; v.nx += triN[t * 3]; v.ny += triN[t * 3 + 1]; v.nz += triN[t * 3 + 2]; v.n++;
    }
    const Qc = 0.1, Qn = 0.05, qg = (val: number, s: number) => Math.round(val / s);
    const imza = (r: number) => { const v = kokVeri.get(r)!; return [qg(v.nx / v.n, Qn), qg(v.ny / v.n, Qn), qg(v.nz / v.n, Qn), qg(v.cx / v.n, Qc), qg(v.cy / v.n, Qc), qg(v.cz / v.n, Qc)]; };
    const kokler = [...kokVeri.keys()].sort((ra, rb) => { const A = imza(ra), B = imza(rb); for (let i = 0; i < 6; i++) if (A[i] !== B[i]) return A[i] - B[i]; return ra - rb; });
    const fidHarita = new Map<number, number>();
    kokler.forEach((r, i) => fidHarita.set(r, i));
    const triFid = new Int32Array(ucgen);
    for (let t = 0; t < ucgen; t++) triFid[t] = fidHarita.get(bul(t))!;

    // 2) Üçgenleri faceId'ye göre grupla → bitişik vertex blokları (non-indexed)
    const fidGruplar = new Map<number, number[]>();
    for (let t = 0; t < ucgen; t++) { let arr = fidGruplar.get(triFid[t]); if (!arr) { arr = []; fidGruplar.set(triFid[t], arr); } arr.push(t); }

    const vc = ucgen * 3;
    const npos = new Float32Array(vc * 3);
    const nnor = nor ? new Float32Array(vc * 3) : null;
    const ngrup = new Float32Array(vc * 3);
    let w = 0;
    for (const [fid, tris] of fidGruplar) {
      const strt = w, syi = tris.length * 3;
      for (const t of tris) {
        for (let k = 0; k < 3; k++) {
          const src = vIdx(t, k);
          npos[w * 3] = pos.getX(src); npos[w * 3 + 1] = pos.getY(src); npos[w * 3 + 2] = pos.getZ(src);
          if (nnor && nor) { nnor[w * 3] = nor.getX(src); nnor[w * 3 + 1] = nor.getY(src); nnor[w * 3 + 2] = nor.getZ(src); }
          ngrup[w * 3] = strt; ngrup[w * 3 + 1] = syi; ngrup[w * 3 + 2] = fid;
          w++;
        }
      }
    }

    geo.setIndex(null);
    geo.setAttribute('position', new THREE.Float32BufferAttribute(npos, 3));
    if (nnor) geo.setAttribute('normal', new THREE.Float32BufferAttribute(nnor, 3));
    else geo.computeVertexNormals();
    geo.setAttribute('grup', new THREE.Float32BufferAttribute(ngrup, 3));
  }

  /**
   * Gerçek B-Rep kenarları: üçgen kenarlarını POZİSYONA göre eşleştirir
   * (vertex index'e değil → CSG'nin kaynaklanmamış vertex kaosundan etkilenmez),
   * iki komşu üçgenin normal açısı > aciDeg ise kenar kabul eder.
   * Koplanar üçgen ağı ve eğri facet çizgileri gizlenir; sadece kutu köşeleri,
   * delik ağzı gibi keskin yüz-geçiş kenarları kalır.
   */
  kenarCikar(geo: THREE.BufferGeometry, aciDeg = 30): THREE.BufferGeometry {
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const idx = geo.index;
    const ucgen = idx ? idx.count / 3 : pos.count / 3;
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
    const e1 = new THREE.Vector3(), e2 = new THREE.Vector3(), n = new THREE.Vector3();
    const Q = 1e-3;
    const q = (v: number) => Math.round(v / Q);
    const harita = new Map<string, { p: number[]; ns: number[][]; ka: string; kb: string }>();
    const vnorm = new Map<string, number[][]>();  // vertex → komşu üçgen normalleri (T-junction kurtarma)

    for (let t = 0; t < ucgen; t++) {
      const ia = idx ? idx.getX(t * 3) : t * 3;
      const ib = idx ? idx.getX(t * 3 + 1) : t * 3 + 1;
      const ic = idx ? idx.getX(t * 3 + 2) : t * 3 + 2;
      a.fromBufferAttribute(pos, ia); b.fromBufferAttribute(pos, ib); c.fromBufferAttribute(pos, ic);
      e1.subVectors(b, a); e2.subVectors(c, a); n.crossVectors(e1, e2);
      if (n.lengthSq() < 1e-14) continue;
      n.normalize();
      const nn = [n.x, n.y, n.z];
      const noktalar = [a, b, c];
      const keyler = noktalar.map(v => `${q(v.x)},${q(v.y)},${q(v.z)}`);
      for (const kk of keyler) { let arr = vnorm.get(kk); if (!arr) { arr = []; vnorm.set(kk, arr); } arr.push(nn); }
      for (let k = 0; k < 3; k++) {
        const v1 = noktalar[k], v2 = noktalar[(k + 1) % 3];
        const ka = keyler[k], kb = keyler[(k + 1) % 3];
        const key = ka < kb ? ka + '|' + kb : kb + '|' + ka;
        let e = harita.get(key);
        if (!e) { e = { p: [v1.x, v1.y, v1.z, v2.x, v2.y, v2.z], ns: [], ka, kb }; harita.set(key, e); }
        e.ns.push(nn);
      }
    }

    const cosThr = Math.cos(aciDeg * Math.PI / 180);
    const farkliMi = (a: number[], list: number[][]) => {
      for (const b of list) if (a[0] * b[0] + a[1] * b[1] + a[2] * b[2] < cosThr) return true;
      return false;
    };
    const cizgi: number[] = [];
    for (const e of harita.values()) {
      let kenar = false;
      if (e.ns.length >= 2) {
        // normal komşuluk: iki üçgen normali arasındaki açı > eşik → keskin kenar
        for (let i = 0; i < e.ns.length && !kenar; i++)
          for (let j = i + 1; j < e.ns.length; j++) {
            const d = e.ns[i][0] * e.ns[j][0] + e.ns[i][1] * e.ns[j][1] + e.ns[i][2] * e.ns[j][2];
            if (d < cosThr) { kenar = true; break; }
          }
      } else {
        // tek-kullanımlı (CSG T-junction): uçlarında farklı-normalli üçgen varsa GERÇEK
        // keskin kenardır (delik rim'i gibi) → al; flat yüzdeki çöp ise tüm komşular
        // koplanar olduğu için elenir.
        const n0 = e.ns[0];
        kenar = farkliMi(n0, vnorm.get(e.ka) || []) && farkliMi(n0, vnorm.get(e.kb) || []);
      }
      if (kenar) cizgi.push(...e.p);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(cizgi, 3));
    return g;
  }

  /** Profil 2D noktalarını eksen etrafında döndürerek katı (lathe) üretir. */
  static lathe(profil2D: { x: number; y: number }[], segment = 48): THREE.BufferGeometry {
    const noktalar = profil2D.map(p => new THREE.Vector2(Math.max(1e-4, Math.abs(p.x)), p.y));
    const g = new THREE.LatheGeometry(noktalar, segment);
    g.computeVertexNormals();
    return g;
  }
}
